import { memo, useCallback, useEffect, useMemo } from 'react';

import {
  type UseFieldArrayRemove,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import type { AutocompleteSelectProps } from '@pulse-web-ui/autocomplete-select/lib/autocomplete-select.types';
import { isAfter } from 'date-fns';

import {
  AutocompleteSelectController,
  CheckboxController,
  ColumnContainer,
  DaDataPartySuggestionController,
  DatepickerController,
  DynamicFormTitle,
  InputController,
  MonthDatepickerContainer,
  TwoColumnContainer,
} from '@shared/ui';

import { useTranslation } from '../../../helpers';
import type { OtherWorkExperienceFormValues } from './types';
import { getMinStartEndWorkDate } from './utils';

interface CompanyFormProps {
  index: number;
  onRemove?: UseFieldArrayRemove;
}

const minDate = getMinStartEndWorkDate();
const maxDate = new Date();

export const getDismissalReasons = (
  t: ReturnType<typeof useTranslation>
): AutocompleteSelectProps['options'] => [
  {
    label: t('otherWorkExperienceForm.dismissalReasons.employeeInitiative'),
    value: 'EMPLOYEE_INITIATIVE',
  },
  {
    label: t('otherWorkExperienceForm.dismissalReasons.employerInitiative'),
    value: 'EMPLOYER_INITIATIVE',
  },
  {
    label: t('otherWorkExperienceForm.dismissalReasons.circumstances'),
    value: 'CIRCUMSTANCES_BEYOND_CONTROL',
  },
];

const MM_YYYY_MASK = [/\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/];

export const OtherWorkForm = memo(({ index, onRemove }: CompanyFormProps) => {
  const t = useTranslation();
  const {
    control,
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<OtherWorkExperienceFormValues>();

  const dismissalReasons = getDismissalReasons(t);
  const startDate = useWatch({
    name: `workDetails.${index}.startDate`,
  });

  const endDate = useWatch({
    name: `workDetails.${index}.endDate`,
  });

  useEffect(() => {
    if (startDate && endDate && isAfter(startDate, endDate)) {
      setValue(`workDetails.${index}.startDate`, '');
    }
  }, [startDate, endDate]);

  const buttonProps = useMemo(
    () => ({
      onClick: onRemove,
    }),
    [onRemove]
  );

  const handleCompanyNameChange = useCallback(() => {
    if (errors?.workDetails?.[index]?.companyName) {
      clearErrors(`workDetails.${index}.companyName`);
    }
  }, [errors, index]);

  const isCurrentJob = useWatch({
    name: `workDetails.${index}.isCurrentJob`,
  });

  const handleIsCurrentJobChange = useCallback(
    (value: boolean) => {
      if (value) {
        setValue(`workDetails.${index}.endDate`, '');
        if (errors?.workDetails?.[index]?.endDate) {
          clearErrors(`workDetails.${index}.endDate`);
        }
      }
    },
    [errors, index]
  );

  return (
    <ColumnContainer $gap={16}>
      <DynamicFormTitle
        isNewPath
        title={t('otherWorkExperienceForm.labels.workPlace')}
        index={index}
        buttonProps={buttonProps}
        testId='workPlace'
      />
      <TwoColumnContainer $marginBottom={0}>
        <DaDataPartySuggestionController
          control={control}
          name={`workDetails.${index}.companyName`}
          uid='companyName'
          label={t('otherWorkExperienceForm.labels.companyName')}
          onChange={handleCompanyNameChange}
          testId={`workDetails-${index}-companyName`}
        />
        <InputController
          control={control}
          name={`workDetails.${index}.position`}
          label={t('otherWorkExperienceForm.labels.position')}
          id='position'
          testId={`workDetails-${index}-position`}
        />
        <DatepickerController
          control={control}
          name={`workDetails.${index}.startDate`}
          label={t('otherWorkExperienceForm.labels.startDate')}
          testId={`workDetails-${index}-startDate`}
          dateFormat='MM.yyyy'
          showMonthYearPicker
          showFullMonthYearPicker
          renderCustomHeader={undefined}
          calendarContainer={MonthDatepickerContainer}
          hint={t('otherWorkExperienceForm.hints.datepicker')}
          minDate={minDate}
          maxDate={endDate ? new Date(endDate) : maxDate}
          inputMask={MM_YYYY_MASK}
          showInput
        />
        <DatepickerController
          control={control}
          name={`workDetails.${index}.endDate`}
          label={t('otherWorkExperienceForm.labels.endDate')}
          testId={`workDetails-${index}-endDate`}
          dateFormat='MM.yyyy'
          showMonthYearPicker
          showFullMonthYearPicker
          renderCustomHeader={undefined}
          calendarContainer={MonthDatepickerContainer}
          disabled={isCurrentJob}
          hint={t('otherWorkExperienceForm.hints.datepicker')}
          minDate={minDate}
          maxDate={maxDate}
          inputMask={MM_YYYY_MASK}
          showInput
        />
      </TwoColumnContainer>
      <CheckboxController
        control={control}
        name={`workDetails.${index}.isCurrentJob`}
        label={t('otherWorkExperienceForm.labels.isCurrentJob')}
        testId={`workDetails-${index}-isCurrentJob-checkbox`}
        onChange={handleIsCurrentJobChange}
      />
      {!isCurrentJob && (
        <AutocompleteSelectController
          control={control}
          name={`workDetails.${index}.dismissalReason`}
          label={t('otherWorkExperienceForm.labels.dismissalReason')}
          options={dismissalReasons}
          testId={`workDetails-${index}-dismissalReason`}
        />
      )}
    </ColumnContainer>
  );
});
